import { useState } from "react";
import { isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import { api, setToken, type UserOut } from "../api";
import { BrandWordmark } from "../components/brand";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function Login({ onLogin }: { onLogin: (u: UserOut) => void }) {
  const { t } = useTranslation(["login", "common"]);
  const [phone, setPhone] = useState("+998");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isBlankPhoneInput(phone)) { setErr(t("failed")); return; }
    setErr(null);
    setBusy(true);
    try {
      const res = await api.login(phone, password);
      if (res.user.role !== "workshop_owner") {
        setErr(t("ownerOnly"));
        setBusy(false);
        return;
      }
      setToken(res.access_token);
      onLogin(res.user);
    } catch (e) {
      const er = e as { message?: string };
      setErr(er.message || t("failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="pt-8 pb-7 space-y-6">
          <div className="flex flex-col items-center gap-3">
            <div className="size-14 rounded-[8px] bg-[#0A0E18] p-[3px] shadow-lg shadow-primary/25">
              <img
                src="/logo-mark.png"
                alt="AvtoDaftar"
                className="w-full h-full object-cover rounded-[5px]"
              />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight">
                <BrandWordmark />
              </h1>
              <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t("phone")}</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+998901234567"
                required
                autoFocus
                autoComplete="username"
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                disabled={busy}
              />
            </div>
            {err && (
              <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full" size="lg">
              {busy ? t("submitting") : t("submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
